import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, GraduationCap, Star, Library, FileQuestion, FileText, BarChart3, School, CalendarDays, Rocket } from 'lucide-react';
import { Card } from '../components/ui/card';
import { teacherFeatures } from '../mock';
import { FeatureTile } from '../components/FeatureTile';
import axios from 'axios';
import { API } from '../context/AuthContext';

const iconMap = { BookOpen, Star, CalendarDays, School, Library, FileQuestion, FileText, BarChart3 };

export default function TeacherPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => {
    axios.get(`${API}/discover/classes`).then(r => {
      setClasses(r.data);
      if (r.data.length && !selectedClass) setSelectedClass(r.data[0]);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-4 pt-4 space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
           style={{ background: 'linear-gradient(135deg, #059669 0%, #0d9488 55%, #0891b2 100%)' }}>
        <div className="absolute -top-8 -right-8 opacity-20"><BookOpen size={110} /></div>
        <BookOpen size={26} />
        <h1 className="text-xl font-extrabold mt-2">ଶିକ୍ଷକ ସମ୍ବଳ</h1>
        <p className="text-xs text-white/90 font-medium">Teacher Resources — Balvatika to Class 10</p>
        <p className="text-[11px] text-white/85 mt-1">ପାଠ୍ୟ ଯୋଜନା, ପୁସ୍ତକାଳୟ, କାର୍ଯ୍ୟପତ୍ର ଓ ଅଧିକ</p>
      </div>

      {/* Featured CTA — go to browse */}
      <Link to="/teacher/browse" data-testid="teacher-browse-cta"
        className="block rounded-2xl p-4 text-white shadow-md bg-gradient-to-r from-emerald-600 to-cyan-600 hover:opacity-95">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2"><Rocket size={16}/><div className="font-extrabold text-base">🎯 Explore All Resources</div></div>
            <div className="text-xs text-white/85 mt-1">Class → Subject → Topic → Content</div>
          </div>
          <ChevronRight size={20}/>
        </div>
      </Link>

      {/* Class Selector — DB driven */}
      <div>
        <h3 className="text-sm font-bold text-gray-700">ଶ୍ରେଣୀ ବାଛନ୍ତୁ · Select Class</h3>
        <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
          {classes.map((c) => {
            const isActive = selectedClass?.id === c.id;
            return (
              <Link key={c.id} to={`/teacher/browse/${c.slug}`} onClick={() => setSelectedClass(c)} data-testid={`teacher-class-${c.slug}`}
                className={`shrink-0 px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${
                  isActive ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'
                }`}>
                {c.name_odia}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Feature Grid */}
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-extrabold text-gray-900">ସଂପଦ · Resources</h3>
          <span className="text-[11px] font-semibold text-emerald-600">{selectedClass?.name_odia || ''}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {teacherFeatures.map((f) => {
            const Icon = iconMap[f.icon] || BookOpen;
            return (
              <FeatureTile key={f.id} item={f} icon={Icon} gradient={f.gradient} className="min-h-[130px]" testId={`teacher-tile-${f.id}`}>
                {f.badge && <span className="absolute top-2 right-2 text-[9px] font-bold bg-white text-gray-800 px-1.5 py-0.5 rounded-full">{f.badge}</span>}
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon size={19} />
                </div>
                <div>
                  <div className="font-bold text-sm leading-tight">{f.title}</div>
                  <div className="text-[10px] text-white/85 mt-0.5">{f.titleEn}</div>
                  <div className="text-[10px] text-white/75 mt-0.5">{f.soon ? 'ଶୀଘ୍ର ଆସୁଛି · Coming soon' : f.subtitle}</div>
                </div>
              </FeatureTile>
            );
          })}
        </div>
      </div>

      {/* Current Month Preview */}
      <Card className="rounded-2xl p-4 border-gray-200 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold text-emerald-700 uppercase">ଚଳିତ ମାସ · Current Month</div>
            <h4 className="font-extrabold text-lg text-gray-900 mt-0.5">ଜୁଲାଇ (July) ପାଠ୍ୟ ଯୋଜନା</h4>
            <p className="text-[11px] text-gray-600 mt-1">{selectedClass?.name_odia || selectedClass?.name_en || ''} — ବିଷୟଓୟାରୀ ପାଠ ଖସଡ଼ା</p>
          </div>
          <ChevronRight size={20} className="text-emerald-700" />
        </div>
      </Card>
    </div>
  );
}
